import type { NotificationCategory, NotificationIcon } from "@loot-os/api-contract";
import {
  Archive,
  BadgeCheck,
  Bell,
  Box,
  Calendar,
  Clock,
  Coins,
  CreditCard,
  Crown,
  Flame,
  Gift,
  Hammer,
  Heart,
  IdCard,
  LockOpen,
  type LucideIcon,
  Megaphone,
  Package,
  PackagePlus,
  PackageX,
  PartyPopper,
  Percent,
  Rocket,
  ShieldX,
  Sparkles,
  Star,
  Swords,
  Tag,
  Trophy,
  Truck,
  Tv,
  Wallet,
  Zap,
} from "lucide-react";

/** The lucide component for each stored icon name; a template stores the name, never markup. */
export const NOTIFICATION_ICON_COMPONENTS: Record<NotificationIcon, LucideIcon> = {
  bell: Bell,
  truck: Truck,
  package: Package,
  "package-x": PackageX,
  trophy: Trophy,
  swords: Swords,
  hammer: Hammer,
  "package-plus": PackagePlus,
  archive: Archive,
  wallet: Wallet,
  "credit-card": CreditCard,
  gift: Gift,
  "badge-check": BadgeCheck,
  "shield-x": ShieldX,
  "id-card": IdCard,
  "lock-open": LockOpen,
  "party-popper": PartyPopper,
  sparkles: Sparkles,
  megaphone: Megaphone,
  star: Star,
  tag: Tag,
  flame: Flame,
  zap: Zap,
  heart: Heart,
  coins: Coins,
  box: Box,
  calendar: Calendar,
  clock: Clock,
  percent: Percent,
  rocket: Rocket,
  crown: Crown,
  tv: Tv,
};

/**
 * Each category's icon tile, drawn from the theme's chart tokens so a tenant's palette tints it.
 */
export const NOTIFICATION_CATEGORY_TILE: Record<NotificationCategory, string> = {
  orders: "bg-chart-3/15 text-chart-3",
  battles: "bg-chart-2/15 text-chart-2",
  forge: "bg-chart-5/15 text-chart-5",
  live_breaks: "bg-chart-1/15 text-chart-1",
  balance: "bg-chart-4/15 text-chart-4",
  account: "bg-foreground/10 text-foreground",
  marketing: "bg-primary/15 text-primary",
};

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

// Pinned like `formatNumber`, so the dashboard's preview reads as the store does.
const dayMonth = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });
const dayMonthYear = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

/** How long ago, compactly: `now`, `12m`, `3h`, `2d`, then the date (`Sep 28`). */
export function formatNotificationTime(createdAt: string | Date, now: Date): string {
  const at = new Date(createdAt);
  const elapsed = Math.max(0, now.getTime() - at.getTime());
  if (elapsed < MINUTE_MS) return "now";
  if (elapsed < HOUR_MS) return `${Math.floor(elapsed / MINUTE_MS)}m`;
  if (elapsed < DAY_MS) return `${Math.floor(elapsed / HOUR_MS)}h`;
  if (elapsed < 7 * DAY_MS) return `${Math.floor(elapsed / DAY_MS)}d`;
  return (at.getFullYear() === now.getFullYear() ? dayMonth : dayMonthYear).format(at);
}

/** Splits a newest-first list into today's (the viewer's calendar day) and everything earlier. */
export function groupNotificationsByDay<T extends { createdAt: string }>(
  notifications: readonly T[],
  now: Date,
): { today: T[]; earlier: T[] } {
  const today: T[] = [];
  const earlier: T[] = [];
  for (const notification of notifications) {
    const at = new Date(notification.createdAt);
    const sameDay =
      at.getFullYear() === now.getFullYear() &&
      at.getMonth() === now.getMonth() &&
      at.getDate() === now.getDate();
    (sameDay ? today : earlier).push(notification);
  }
  return { today, earlier };
}

/** The bell's badge: nothing at zero, the count up to nine, then `9+`. */
export function formatUnreadBadge(unread: number): string | null {
  if (unread <= 0) return null;
  return unread > 9 ? "9+" : String(unread);
}
